<template>
  <div class="user-statistics-dashboard">
    <div class="page-header mb-4">
      <div>
        <span class="eyebrow">USER ANALYTICS</span>
        <h1 class="page-title">用户多维统计分析</h1>
        <p class="page-subtitle mb-0">基于用户列表同一口径，查看账户、角色、日期、刷题与直接邀请关系。</p>
      </div>
      <router-link to="/admin/users" class="btn btn-outline-primary">返回用户管理</router-link>
    </div>

    <UserStatisticsFilters
      v-model:search-query="searchQuery"
      v-model:role-filter="roleFilter"
      v-model:status-filter="statusFilter"
      v-model:date-field="dateField"
      v-model:start-date="startDate"
      v-model:end-date="endDate"
      :role-options="roleOptions"
      class="mb-4"
      @reset="resetFilters"
    />

    <div v-if="loading" class="state-card">
      <span class="spinner-border spinner-border-sm me-2"></span>正在加载全部用户统计…
    </div>
    <div v-else-if="loadFailed" class="state-card text-danger">
      用户统计加载失败，请刷新后重试。
    </div>
    <template v-else>
      <div class="metric-grid mb-4">
        <article class="metric-card metric-card--blue">
          <span>筛选用户</span>
          <strong>{{ formatNumber(summary.totalUsers) }}</strong>
          <small>全量用户中的当前筛选结果</small>
        </article>
        <article class="metric-card metric-card--teal">
          <span>有刷题记录用户</span>
          <strong>{{ formatNumber(summary.usersWithActivity) }}</strong>
          <small>存在最后活跃时间</small>
        </article>
        <article class="metric-card metric-card--indigo">
          <span>累计刷题数</span>
          <strong>{{ formatNumber(summary.totalQuestions) }}</strong>
          <small>已完成会话题数合计</small>
        </article>
        <article class="metric-card metric-card--rose">
          <span>当前错题数</span>
          <strong>{{ formatNumber(summary.wrongQuestionCount) }}</strong>
          <small>未掌握错题记录合计</small>
        </article>
      </div>

      <div v-if="summary.totalUsers === 0" class="state-card">
        当前条件下没有用户数据，请调整筛选范围。
      </div>
      <template v-else>
        <div class="dashboard-grid mb-4">
          <section class="panel-card">
            <div class="panel-heading">
              <div>
                <h2>角色分布</h2>
                <p>当前筛选用户按既有角色归组</p>
              </div>
            </div>
            <div class="distribution-list">
              <div v-for="role in summary.roles" :key="role.name" class="distribution-row">
                <div class="distribution-label">
                  <span>{{ roleLabel(role.name) }}</span>
                  <strong>{{ role.count }}</strong>
                </div>
                <div class="distribution-track">
                  <span :style="{ width: percentage(role.count, summary.totalUsers) }"></span>
                </div>
              </div>
            </div>
          </section>

          <section class="panel-card">
            <div class="panel-heading">
              <div>
                <h2>账户状态</h2>
                <p>启用状态不等同于有刷题记录</p>
              </div>
            </div>
            <div class="status-chart">
              <div class="status-number status-number--enabled">
                <strong>{{ summary.enabledUsers }}</strong>
                <span>启用</span>
              </div>
              <div class="status-number status-number--disabled">
                <strong>{{ summary.disabledUsers }}</strong>
                <span>禁用</span>
              </div>
            </div>
          </section>
        </div>

        <section class="panel-card mb-4">
          <div class="panel-heading">
            <div>
              <h2>{{ dateField === 'createdAt' ? '注册日期归组' : '最后活跃日期归组' }}</h2>
              <p>每行题数为该日期用户群的累计刷题数，错题数为其当前未掌握错题数。</p>
            </div>
            <span class="dimension-badge">{{ dateFieldLabel }}</span>
          </div>
          <div class="table-responsive">
            <table class="table cohort-table mb-0">
              <thead>
                <tr>
                  <th>日期</th>
                  <th>用户数</th>
                  <th>累计刷题数</th>
                  <th>当前错题数</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cohort in visibleCohorts" :key="cohort.date" data-test="cohort-row">
                  <td>{{ cohort.date }}</td>
                  <td>{{ formatNumber(cohort.userCount) }}</td>
                  <td>{{ formatNumber(cohort.totalQuestions) }}</td>
                  <td>{{ formatNumber(cohort.wrongQuestionCount) }}</td>
                </tr>
                <tr v-if="cohorts.length === 0">
                  <td colspan="4" class="text-center text-muted py-4">没有可归组的日期数据</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="hasMoreCohorts" class="cohort-actions">
            <button
              type="button"
              class="btn btn-outline-primary btn-sm"
              data-test="load-more-cohorts"
              @click="showMoreCohorts"
            >
              更多
            </button>
            <span>再显示更早的 {{ Math.min(COHORT_PAGE_SIZE, cohorts.length - visibleCohortCount) }} 条记录</span>
          </div>
        </section>

        <ReferralAnalyticsPanels
          :filtered-users="filteredUsers"
          :all-users="users"
          @select-user="selectedUserId = $event"
        />

        <section class="panel-card">
          <div class="panel-heading">
            <div>
              <h2>活跃用户</h2>
              <p>当前筛选结果按{{ practicePeriodLabel }}已完成会话题数降序展示，最多 10 位。</p>
            </div>
            <label class="practice-period-control">
              <span>统计时间段</span>
              <select v-model="practicePeriod" class="form-select form-select-sm" data-test="practice-period-select">
                <option v-for="option in practicePeriodOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </label>
          </div>
          <div class="table-responsive">
            <table class="table top-users-table mb-0">
              <thead>
                <tr>
                  <th>用户</th>
                  <th>角色</th>
                  <th>{{ practicePeriodLabel }}刷题数</th>
                  <th>当前错题数</th>
                  <th>最后活跃</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in topUsers" :key="user.id">
                  <td>
                    <strong>{{ user.username }}</strong>
                    <small class="d-block text-muted">{{ user.firstName || '' }} {{ user.lastName || '' }}</small>
                    <button type="button" class="user-id-link" @click="selectedUserId = user.id">
                      用户 ID：{{ user.id }}
                    </button>
                  </td>
                  <td>{{ roleLabel(getRoleName(user.role)) }}</td>
                  <td>{{ formatNumber(getPracticeQuestionCount(user, practicePeriod)) }}</td>
                  <td>{{ formatNumber(user.activityStats?.wrongQuestionCount || 0) }}</td>
                  <td>{{ formatTime(user.activityStats?.lastActiveTime) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </template>
    </template>
    <UserStatisticsUserDetail :user="selectedUser" @close="selectedUserId = null" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { usersApi, type Role } from '@/api/users'
import ReferralAnalyticsPanels from '@/components/admin/ReferralAnalyticsPanels.vue'
import UserStatisticsFilters from '@/components/admin/UserStatisticsFilters.vue'
import UserStatisticsUserDetail from '@/components/admin/UserStatisticsUserDetail.vue'
import type { PracticePeriod, UserStatisticsFilterState, UserWithActivity } from '@/types/user-statistics'
import {
  buildUserDateCohorts,
  filterAndSortUsers,
  getPracticeQuestionCount,
  getTopPracticeUsers,
  summarizeUsers,
} from '@/utils/user-statistics'

const users = ref<UserWithActivity[]>([])
const roleOptions = ref<Role[]>([])
const loading = ref(true)
const loadFailed = ref(false)
const searchQuery = ref('')
const roleFilter = ref('')
const statusFilter = ref<UserStatisticsFilterState['statusFilter']>('')
const dateField = ref<UserStatisticsFilterState['dateField']>('createdAt')
const startDate = ref('')
const endDate = ref('')
const practicePeriod = ref<PracticePeriod>('1d')
const selectedUserId = ref<number | null>(null)
const INITIAL_COHORT_COUNT = 10
const COHORT_PAGE_SIZE = 20
const visibleCohortCount = ref(INITIAL_COHORT_COUNT)
const practicePeriodOptions: Array<{ value: PracticePeriod; label: string }> = [
  { value: '1d', label: '最近一天' },
  { value: '7d', label: '最近 7 天' },
  { value: '30d', label: '最近一月' },
  { value: 'all', label: '全部时间' },
]

const getRoleName = (role: UserWithActivity['role']) => typeof role === 'string' ? role : role?.name || ''
const roleLabel = (role: string) => role === 'admin' ? '管理员' : role === 'user' ? '普通用户' : role || '未知角色'

const filteredUsers = computed(() => filterAndSortUsers(users.value, {
  searchQuery: searchQuery.value,
  roleFilter: roleFilter.value,
  statusFilter: statusFilter.value,
  dateField: dateField.value,
  startDate: startDate.value,
  endDate: endDate.value,
  sortBy: '',
  sortOrder: 'desc',
}, getRoleName))

const summary = computed(() => summarizeUsers(filteredUsers.value, getRoleName))
const cohorts = computed(() => buildUserDateCohorts(filteredUsers.value, dateField.value))
const visibleCohorts = computed(() => dateField.value === 'createdAt'
  ? cohorts.value.slice(0, visibleCohortCount.value)
  : cohorts.value)
const hasMoreCohorts = computed(() => (
  dateField.value === 'createdAt' && visibleCohortCount.value < cohorts.value.length
))
const topUsers = computed(() => getTopPracticeUsers(filteredUsers.value, 10, practicePeriod.value))
const selectedUser = computed(() => users.value.find(user => user.id === selectedUserId.value) || null)
const dateFieldLabel = computed(() => dateField.value === 'createdAt' ? '注册日期' : '最后活跃')
const practicePeriodLabel = computed(() => (
  practicePeriodOptions.find(option => option.value === practicePeriod.value)?.label || '全部时间'
))

const percentage = (part: number, total: number) => total > 0 ? `${Math.round(part / total * 100)}%` : '0%'
const formatNumber = (value: number) => new Intl.NumberFormat('zh-CN').format(value)
const formatTime = (value: string | Date | null | undefined) => value
  ? new Date(value).toLocaleString('zh-CN', { hour12: false })
  : '-'
const showMoreCohorts = () => {
  visibleCohortCount.value += COHORT_PAGE_SIZE
}

watch(cohorts, () => {
  visibleCohortCount.value = INITIAL_COHORT_COUNT
})

const resetFilters = () => {
  searchQuery.value = ''
  roleFilter.value = ''
  statusFilter.value = ''
  dateField.value = 'createdAt'
  startDate.value = ''
  endDate.value = ''
  practicePeriod.value = '1d'
}

onMounted(async () => {
  try {
    const [usersData, rolesData] = await Promise.all([
      usersApi.getUsersWithActivityStats(),
      usersApi.getRoles(),
    ])
    users.value = usersData
    roleOptions.value = rolesData
  } catch (error) {
    loadFailed.value = true
    console.error('加载用户统计看板失败:', error)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.user-statistics-dashboard {
  min-height: 100vh;
  padding: 1.5rem;
  color: #0f172a;
  background: #f4f7fb;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.75rem 2rem;
  color: #fff;
  background: linear-gradient(135deg, #0f3c5c, #0b7285);
  border-radius: 16px;
  box-shadow: 0 18px 36px rgba(15, 60, 92, 0.18);
}

.eyebrow {
  display: block;
  margin-bottom: 0.35rem;
  color: #99f6e4;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
}

.page-title {
  margin: 0 0 0.35rem;
  font-size: 1.9rem;
  font-weight: 750;
}

.page-subtitle {
  color: #dbeafe;
}

.page-header .btn {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.75);
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.metric-card,
.panel-card,
.state-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.metric-card {
  position: relative;
  overflow: hidden;
  padding: 1.3rem 1.4rem;
}

.metric-card::before {
  position: absolute;
  inset: 0 auto 0 0;
  width: 5px;
  content: '';
  background: var(--accent);
}

.metric-card--blue { --accent: #2563eb; }
.metric-card--teal { --accent: #0d9488; }
.metric-card--indigo { --accent: #6366f1; }
.metric-card--rose { --accent: #e11d48; }

.metric-card span,
.metric-card small {
  display: block;
  color: #64748b;
}

.metric-card strong {
  display: block;
  margin: 0.3rem 0 0.15rem;
  font-size: 2rem;
  line-height: 1.1;
}

.metric-card small {
  font-size: 0.78rem;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.65fr);
  gap: 1rem;
}

.panel-card {
  padding: 1.4rem;
}

.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.panel-heading h2 {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
}

.panel-heading p {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.82rem;
}

.dimension-badge {
  padding: 0.35rem 0.65rem;
  color: #0f766e;
  font-size: 0.76rem;
  font-weight: 700;
  background: #ccfbf1;
  border-radius: 999px;
}

.practice-period-control {
  display: grid;
  min-width: 9rem;
  gap: 0.3rem;
  color: #475569;
  font-size: 0.74rem;
  font-weight: 700;
}

.cohort-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding-top: 1rem;
  color: #64748b;
  font-size: 0.78rem;
}

.user-id-link {
  display: block;
  padding: 0;
  color: #2563eb;
  font-size: 0.75rem;
  background: none;
  border: 0;
}

.user-id-link:hover,
.user-id-link:focus-visible {
  text-decoration: underline;
}

.distribution-row + .distribution-row {
  margin-top: 1rem;
}

.distribution-label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.4rem;
  color: #334155;
}

.distribution-track {
  height: 9px;
  overflow: hidden;
  background: #e2e8f0;
  border-radius: 999px;
}

.distribution-track span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #0d9488, #2dd4bf);
  border-radius: inherit;
}

.status-chart {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.85rem;
}

.status-number {
  padding: 1.25rem;
  text-align: center;
  border-radius: 12px;
}

.status-number strong,
.status-number span {
  display: block;
}

.status-number strong {
  font-size: 1.8rem;
}

.status-number--enabled {
  color: #047857;
  background: #ecfdf5;
}

.status-number--disabled {
  color: #be123c;
  background: #fff1f2;
}

.cohort-table th,
.top-users-table th {
  color: #475569;
  font-size: 0.78rem;
  text-transform: uppercase;
  background: #f8fafc;
}

.cohort-table td,
.cohort-table th,
.top-users-table td,
.top-users-table th {
  padding: 0.8rem;
  vertical-align: middle;
  border-color: #e2e8f0;
}

.state-card {
  padding: 2.5rem;
  text-align: center;
  color: #64748b;
}

@media (max-width: 992px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 576px) {
  .user-statistics-dashboard {
    padding: 1rem;
  }

  .page-header {
    align-items: flex-start;
    flex-direction: column;
    padding: 1.4rem;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }
}
</style>
